import React, { useState } from 'react';
import { useAccountingReport } from './useAccountingReport';
import { useAuth } from '../../context/AuthContext';
import moment from 'moment';
import DetalleCuentaModal from './DetalleCuentaModal'; // Reuse for drill-down
import ExportButton from '../common/ExportButton';
import { exportToCSV, exportToExcel, exportToPDF } from '../../utils/exportUtils';
// eslint-disable-next-line no-unused-vars
import { ACCOUNTS } from '../../utils/accountingUtils';

function FlujoEfectivo({ month, year }) {
    const { userData } = useAuth();

    // Date Range: For Cash Flow, we strictly look at movements WITHIN the period.
    const { startDate, endDate } = React.useMemo(() => ({
        startDate: moment().year(year).month(month).startOf('month').toDate(),
        endDate: moment().year(year).month(month).endOf('month').toDate()
    }), [year, month]);

    const { loading, accounts, ledgerEntries } = useAccountingReport(
        userData?.condominioId,
        startDate,
        endDate
    );

    // Modal State
    const [selectedDetail, setSelectedDetail] = useState(null); // { title: string, entries: [] }

    const handleDetailClick = (title, entries) => {
        // We artificially create an "account" object to reuse DetalleCuentaModal
        // or we create a simple modal. Let's reuse Detalle if we can, 
        // but Detalle expects an 'account' prop. 
        // Better: Reuse the entries logic but maybe make a custom view?
        // Actually, DetalleCuentaModal takes `entries`. 
        // We can pass a dummy account object.
        setSelectedDetail({
            account: { numero: '', nombre: title, naturaleza: 'DEUDORA' },
            entries: entries
        });
    };

    // --- PROCESSING LOGIC ---
    // Direct Method: Analyze movements in Bank/Cash accounts.

    // 1. Identify Cash Accounts (Activo Circulante -> Caja/Bancos)
    // We'll use a heuristic: Accounts starting with '10' (standard) or type='ACTIVO' named 'Banco'/'Caja'
    // Ideally we use ACCOUNTS.BANCOS but that might be just one ID.
    // Let's filter accounts where sub-type is 'Efectivo' or just use '1020' (Bancos) and '1010' (Caja).

    const cashAccountIds = accounts
        .filter(a => a.esCuentaEfectivo || a.numero.startsWith('101') || a.numero.startsWith('102') || a.nombre.toLowerCase().includes('banco') || a.nombre.toLowerCase().includes('caja'))
        .map(a => a.id);

    // Structure for Report
    const flow = {
        operacion: {
            entradas: [],
            salidas: []
        },
        inversion: {
            entradas: [],
            salidas: []
        },
        financiamiento: {
            entradas: [],
            salidas: []
        },
        initialBalance: 0,
        finalBalance: 0
    };

    const groupedMovements = {
        'Cobranza a Residentes': [],
        'Otros Ingresos Operativos': [],
        'Pago a Proveedores y Servicios': [],
        'Pago de Nómina': [],
        'Adquisición de Activos': [],
        'Rendimientos Financieros': [],
        'Aportaciones de Capital': [],
        'Préstamos': [],
        'Otros': []
    };

    // Helper to bucket movements
    const classifyMovement = (entry, contraAccount, amount, target) => {
        // target: 'entradas' | 'salidas'
        // contraAccount: The OTHER side of the transaction.

        if (contraAccount?.clasificacionFlujo === 'excluir') {
            return false;
        }

        let category = 'Otros';
        let section = 'operacion';

        if (!contraAccount) {
            category = 'Movimientos Interbancarios/Ajustes'; // Transfers between banks ignored?
            // If contra is also a cash account, strict cash flow ignores it (net 0).
            if (cashAccountIds.includes(contraAccount?.id)) return false;
        }

        const type = contraAccount?.tipo || 'OTRO';
        const name = contraAccount?.nombre || '';
        const number = contraAccount?.numero || '';

        // --- CLASSIFICATION RULES ---

        // Prioritize explicit account classification
        if (contraAccount?.clasificacionFlujo && contraAccount.clasificacionFlujo !== 'default') {
            section = contraAccount.clasificacionFlujo;
            category = contraAccount.nombre;
        } else {
            // Fallback to system heuristics
            if (target === 'entradas') {
                if (number.startsWith('105') || name.includes('Client') || name.includes('Cuotas')) {
                    category = 'Cobranza a Residentes';
                } else if (type === 'INGRESO') {
                    category = 'Otros Ingresos Operativos';
                } else if (type === 'PASIVO') {
                    section = 'financiamiento';
                    category = 'Préstamos Recibidos';
                } else if (type === 'CAPITAL') {
                    section = 'financiamiento';
                    category = 'Aportaciones de Capital';
                }
            } else { // salidas
                if (type === 'GASTO') {
                    category = 'Pago a Proveedores y Servicios';
                } else if (type === 'PASIVO') {
                    category = 'Pago de Pasivos/Proveedores';
                } else if (type === 'ACTIVO' && !number.startsWith('10')) {
                    section = 'inversion';
                    category = 'Adquisición de Activos';
                }
            }
        }

        // Push to buckets
        if (target === 'entradas') {
            flow[section].entradas.push({ ...entry, category, amount });
        } else {
            flow[section].salidas.push({ ...entry, category, amount });
        }

        // Add to flat group for drill-down
        if (!groupedMovements[category]) groupedMovements[category] = [];
        groupedMovements[category].push({ ...entry, debe: target === 'salidas' ? amount : 0, haber: target === 'entradas' ? amount : 0 });

        return true;
    };

    let totalEntradas = 0;
    let totalSalidas = 0;

    if (!loading) {
        ledgerEntries.forEach(asiento => {
            if (!asiento.detalles) return;

            // Analyze each detail line
            // If line matches CASH account
            asiento.detalles.forEach(det => {
                if (cashAccountIds.includes(det.cuentaId)) {
                    // This is the Cash leg.
                    const isDebit = det.debe > 0; // Money In (Asset Increase)
                    const amount = isDebit ? det.debe : det.haber;

                    // Find Contra-Account (Simple heuristic: the largest OTHER line in the entry)
                    const others = asiento.detalles.filter(d => d !== det);
                    const contraDetail = others.length > 0 ? others[0] : null;
                    const contraAccount = accounts.find(a => a.id === contraDetail?.cuentaId);

                    if (isDebit) {
                        const processed = classifyMovement({ ...asiento, ...det }, contraAccount, amount, 'entradas');
                        if (processed) totalEntradas += amount;
                    } else {
                        const processed = classifyMovement({ ...asiento, ...det }, contraAccount, amount, 'salidas');
                        if (processed) totalSalidas += amount;
                    }
                }
            });
        });
    }

    // Helper to sum categories
    const sumCategory = (list) => list.reduce((sum, item) => sum + item.amount, 0);

    // --- RENDER HELPERS ---
    const renderSection = (title, data) => {
        const entradasGroups = {};
        data.entradas.forEach(e => {
            entradasGroups[e.category] = (entradasGroups[e.category] || 0) + e.amount;
        });

        const salidasGroups = {};
        data.salidas.forEach(e => {
            salidasGroups[e.category] = (salidasGroups[e.category] || 0) + e.amount;
        });

        const sectionNet = sumCategory(data.entradas) - sumCategory(data.salidas);

        return (
            <div style={{ marginBottom: '20px' }}>
                <h3 style={{ background: '#f3f4f6', padding: '10px', borderBottom: '2px solid #ddd' }}>{title}</h3>

                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                        {Object.entries(entradasGroups).map(([cat, val]) => (
                            <tr key={cat} className="report-row" onClick={() => handleDetailClick(cat, groupedMovements[cat])}>
                                <td style={{ padding: '8px' }}>{cat}</td>
                                <td style={{ padding: '8px', textAlign: 'right', color: '#059669' }}>
                                    + {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(val)}
                                </td>
                            </tr>
                        ))}
                        {Object.entries(salidasGroups).map(([cat, val]) => (
                            <tr key={cat} className="report-row" onClick={() => handleDetailClick(cat, groupedMovements[cat])}>
                                <td style={{ padding: '8px' }}>{cat}</td>
                                <td style={{ padding: '8px', textAlign: 'right', color: '#dc2626' }}>
                                    - {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(val)}
                                </td>
                            </tr>
                        ))}
                        <tr style={{ fontWeight: 'bold', background: '#fafafa' }}>
                            <td style={{ padding: '8px', borderTop: '1px solid #ddd' }}>Flujo Neto de {title}</td>
                            <td style={{ padding: '8px', textAlign: 'right', borderTop: '1px solid #ddd', color: sectionNet >= 0 ? '#059669' : '#dc2626' }}>
                                {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(sectionNet)}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    };

    // EXPORTS
    const getExportData = () => {
        // Flatten for CSV/Excel
        const data = [];
        ['operacion', 'inversion', 'financiamiento'].forEach(sec => {
            flow[sec].entradas.forEach(e => data.push({ Seccion: sec, Tipo: 'Entrada', Categoria: e.category, Monto: e.amount }));
            flow[sec].salidas.forEach(e => data.push({ Seccion: sec, Tipo: 'Salida', Categoria: e.category, Monto: e.amount * -1 }));
        });
        return data;
    };

    const handleExportCSV = () => exportToCSV(getExportData(), `Flujo_Efectivo_${month + 1}_${year}`);
    const handleExportExcel = () => exportToExcel(getExportData(), `Flujo_Efectivo_${month + 1}_${year}`, 'Flujo');
    const handleExportPDF = () => {
        const columns = ['Sección', 'Concepto', 'Monto'];
        const rows = [];

        const addPdfSection = (name, key) => {
            rows.push([{ content: name, colSpan: 3, styles: { fillColor: [240, 240, 240], fontStyle: 'bold' } }]);

            // Aggregate for PDF cleanliness
            const summary = {};
            flow[key].entradas.forEach(e => summary[e.category] = (summary[e.category] || 0) + e.amount);
            flow[key].salidas.forEach(e => summary[e.category] = (summary[e.category] || 0) - e.amount);

            Object.entries(summary).forEach(([k, v]) => {
                rows.push(['', k, new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(v)]);
            });
        };

        addPdfSection('ACTIVIDADES DE OPERACIÓN', 'operacion');
        addPdfSection('ACTIVIDADES DE INVERSIÓN', 'inversion');
        addPdfSection('ACTIVIDADES DE FINANCIAMIENTO', 'financiamiento');

        // Net Row
        const net = (totalEntradas - totalSalidas);
        rows.push([{ content: 'FLUJO NETO DEL PERIODO', colSpan: 2, styles: { fontStyle: 'bold' } }, { content: new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(net), styles: { fontStyle: 'bold' } }]);

        exportToPDF(`Flujo de Efectivo - ${month + 1}/${year}`, columns, rows, `Flujo_Efectivo_${month + 1}_${year}`);
    };

    const netCashFlow = totalEntradas - totalSalidas;

    if (loading) return <div>Cargando Flujo de Efectivo...</div>;

    return (
        <div className="report-container">
            {selectedDetail && (
                <DetalleCuentaModal
                    account={selectedDetail.account}
                    entries={selectedDetail.entries.map(e => ({
                        // Adapt to expected format
                        ...e,
                        concepto: e.category + ' - ' + (e.descripcion || e.concepto),
                        debe: e.debe, // Already mapped logic above? No, used raw
                        haber: e.haber
                    }))}
                    onClose={() => setSelectedDetail(null)}
                />
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
                <ExportButton
                    onExportCSV={handleExportCSV}
                    onExportExcel={handleExportExcel}
                    onExportPDF={handleExportPDF}
                />
            </div>

            <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                <div style={{ padding: '20px', background: '#f8fafc', borderRadius: '8px', textAlign: 'center', marginBottom: '30px', border: '1px solid #e2e8f0' }}>
                    <h2 style={{ margin: 0, color: '#334155' }}>Flujo Neto del Periodo</h2>
                    <span style={{ fontSize: '2.5em', fontWeight: 'bold', color: netCashFlow >= 0 ? '#059669' : '#dc2626' }}>
                        {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(netCashFlow)}
                    </span>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '30px', marginTop: '10px', fontSize: '1.1em' }}>
                        <span style={{ color: '#059669' }}>Entradas: {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(totalEntradas)}</span>
                        <span style={{ color: '#dc2626' }}>Salidas: {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(totalSalidas)}</span>
                    </div>
                </div>

                {renderSection('ACTIVIDADES DE OPERACIÓN', flow.operacion)}
                {renderSection('ACTIVIDADES DE INVERSIÓN', flow.inversion)}
                {renderSection('ACTIVIDADES DE FINANCIAMIENTO', flow.financiamiento)}
            </div>
        </div>
    );
}

export default FlujoEfectivo;
